import { Button } from "@/components/ui/button";
import { CodeInput } from "@/components/ui/code-input";
import { FloatingInput } from "@/components/ui/floating-input";
import { api, edenFetch } from "@/lib/eden";
import { queryClient } from "@/lib/queryClient";
import { useReloadHold } from "@/lib/reload-guard";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { emailLinkReturnPath, isValidEmail, mapAuthError, sanitizeNext } from "./auth-utils";
import { FormErrorMessage } from "./form-error";
import { PasswordField } from "./password-field";

type Phase = "email" | "code" | "password";

/**
 * Where the flow was, per tab (2026-10-23). Members switch to their mail app for the code
 * — Yahoo/Outlook can take minutes — and came back to an empty email box (a reload, the
 * tab discarded in the background) with no idea where the code goes. Only the step,
 * address and send time are kept: the reset token stays in memory, so a reload on the
 * new-password step falls back to the code step (resend if that code was already used).
 */
const STORAGE_KEY = "bday_forgot_flow";
/** Reset codes are good for 60 minutes (server PASSWORD_RESET_CODE_EXPIRY_MS). */
const RESUME_WINDOW_MS = 60 * 60 * 1000;
/** Asking again inside this window is answered client-side, without another email. */
const RESEND_QUIET_MS = 30 * 1000;

interface StoredFlow {
  email: string;
  /** Last time WE asked the server to send a code (0 = "I already have a code"). */
  sentAt: number;
}

function readFlow(): StoredFlow | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<StoredFlow> & { at?: number };
    if (typeof v.email !== "string" || typeof v.sentAt !== "number") return null;
    const touched = Math.max(v.sentAt, v.at ?? 0);
    if (Date.now() - touched > RESUME_WINDOW_MS) return null;
    return { email: v.email, sentAt: v.sentAt };
  } catch {
    return null;
  }
}

function writeFlow(flow: StoredFlow | null) {
  try {
    if (flow) sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...flow, at: Date.now() }));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked (private mode): the flow still works, it just won't survive a reload.
  }
}

/** A reset this tab started within the last hour (the authwall reopens on it). */
export function hasPendingForgotFlow(): boolean {
  return readFlow() !== null;
}

const sameEmail = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The `forgot` stage of the auth wall.
 *
 * Legacy emailed a magic reset *link* and the page ended at "we sent you an
 * email". The new server issues a 7-digit *code* instead (see
 * `/public/auth/email/verify-reset-code`), so the flow continues in-page:
 * email → code → new password, then straight into a session. Wording and
 * chrome follow the legacy forgot screen; the extra steps are the ones the
 * new backend requires.
 *
 * Used by the `/forgot` page and, with `onDone`, inside the composer's AuthwallDialog
 * (no navigation: the reset response already carries the new session cookie).
 */
export function ForgotPasswordForm({
  onBack,
  notice,
  onDone,
  defaultEmail,
}: {
  onBack: () => void;
  /** Explanatory line above the form (e.g. a retired legacy reset link, PRD 13 O19). */
  notice?: string;
  /**
   * In-place mode (the authwall): on success refresh the session query and call this
   * instead of navigating — the caller resumes whatever the member was doing.
   */
  onDone?: () => void;
  /** Prefill for the email step (e.g. what they typed into the login form). */
  defaultEmail?: string;
}) {
  // Reopen on the code step when this tab asked for a code within the last hour.
  const [resumed] = useState(readFlow);
  const [phase, setPhase] = useState<Phase>(resumed ? "code" : "email");
  const [email, setEmail] = useState(resumed?.email ?? defaultEmail ?? "");
  const [lastSent, setLastSent] = useState<StoredFlow | null>(resumed);
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  // Informational line on the code step (resent / just sent) — not an error.
  const [info, setInfo] = useState("");
  // Mid-flow: a new build must not reload this away (use-version-check).
  useReloadHold();

  const justSentTo = (to: string) =>
    !!lastSent &&
    lastSent.sentAt > 0 &&
    sameEmail(lastSent.email, to) &&
    Date.now() - lastSent.sentAt < RESEND_QUIET_MS;
  const slowMailHint = (to: string) =>
    `We just sent a code to ${to} \u2014 check your inbox (and spam). Codes can take a few minutes with Yahoo/Outlook.`;

  const sendReset = useMutation({
    mutationFn: (_kind: "first" | "resend") =>
      edenFetch(
        api.api.public.auth.email["forgot-password"].post({
          email,
          // BDAY-479: where the reset email's button brings them back.
          return_to: emailLinkReturnPath(),
        }),
      ),
    onSuccess: (_data, kind) => {
      const flow = { email: email.trim(), sentAt: Date.now() };
      setLastSent(flow);
      writeFlow(flow);
      setPhase("code");
      setError("");
      setInfo(kind === "resend" ? "Sent \u2014 your earlier code still works too." : "");
    },
    onError: (err: Error) => setError(mapAuthError(err.message)),
  });

  /** Ask for a code — unless we asked for this same address seconds ago. */
  const requestCode = (kind: "first" | "resend") => {
    if (justSentTo(email)) {
      setPhase("code");
      setError("");
      setInfo(slowMailHint(email.trim()));
      return;
    }
    sendReset.mutate(kind);
  };

  const verifyCode = useMutation({
    mutationFn: () =>
      edenFetch(api.api.public.auth.email["verify-reset-code"].post({ email, code })),
    onSuccess: (data) => {
      setResetToken(data.reset_token);
      setPhase("password");
      setError("");
    },
    onError: (err: Error) => setError(mapAuthError(err.message)),
  });

  const resetPassword = useMutation({
    mutationFn: () =>
      edenFetch(
        api.api.public.auth.email["reset-password"].post({
          reset_token: resetToken,
          new_password: newPassword,
          platform: "web",
        }),
      ),
    // The server both rotates the password and issues a session cookie. Hard
    // navigation (rather than invalidating like login does) because every other
    // session for this user was just deleted server-side and a full reload is
    // the cleanest way to come up on the new one — but honour `?next=` so a
    // deep link survives the reset.
    onSuccess: () => {
      writeFlow(null);
      if (onDone) {
        // In place (authwall): the response set the session cookie; the refreshed `/me`
        // is what flips the page signed in, exactly like the dialog's email login.
        void queryClient.invalidateQueries({ queryKey: ["/api/user/auth/me"] });
        onDone();
        return;
      }
      const next = sanitizeNext(new URLSearchParams(window.location.search).get("next"));
      window.location.href = next ?? "/";
    },
    onError: (err: Error) => setError(mapAuthError(err.message)),
  });

  const submitPassword = () => {
    if (newPassword.length < 8) return setError("Password must be at least 8 characters");
    if (newPassword !== confirmPassword) return setError("Passwords do not match");
    resetPassword.mutate();
  };

  const buttonClass = "min-h-[39px] w-[199px] text-sm tracking-normal normal-case";

  return (
    <div className="flex w-full max-w-[450px] flex-col items-center">
      {notice && phase === "email" && (
        <p
          className="mb-4 w-full rounded bg-[#fff4e5] px-4 py-3 text-sm leading-[20px] text-[#663c00]"
          data-testid="forgot-notice"
        >
          {notice}
        </p>
      )}
      {phase === "email" && (
        <form
          noValidate
          className="flex w-full flex-col items-center"
          onSubmit={(e) => {
            e.preventDefault();
            if (!isValidEmail(email)) return setError("Email is invalid");
            setError("");
            requestCode("first");
          }}
        >
          <div className="mb-[15px] w-full">
            <FloatingInput
              label="EMAIL"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              data-testid="input-email-forgot"
            />
          </div>
          <Button
            type="submit"
            size="compact"
            className={buttonClass}
            disabled={!email || sendReset.isPending}
            data-testid="button-reset-password"
          >
            {sendReset.isPending ? "Sending..." : "Reset Password"}
          </Button>
          {error && <FormErrorMessage>{error}</FormErrorMessage>}
          {/* Under the button, small: a code is coming. The "next screen" link is the old
              "I already have a code" jump: back from the mail app in another tab or
              device, go straight to the code box for this address (BDAY-475). */}
          <p className="mt-3 text-center text-sm text-muted-foreground" data-testid="forgot-intro">
            We&rsquo;ll email you a security code. Already have one?{" "}
            <button
              type="button"
              onClick={() => {
                if (!isValidEmail(email)) return setError("Enter the email the code was sent to");
                const flow = { email: email.trim(), sentAt: lastSent?.sentAt ?? 0 };
                writeFlow(flow);
                setPhase("code");
                setError("");
                setInfo("");
              }}
              className="text-primary underline"
              data-testid="button-have-code"
            >
              Enter it on the next screen.
            </button>
          </p>
        </form>
      )}

      {phase === "code" && (
        <div className="flex w-full flex-col items-center gap-4">
          <p className="text-center">
            If an account exists for {email}, we&rsquo;ve emailed it a 7-digit code. Enter it below.
          </p>
          <p className="text-center text-sm text-muted-foreground" data-testid="forgot-code-info">
            {info ||
              "Codes can take a few minutes with Yahoo/Outlook \u2014 check your spam folder too."}
          </p>
          <CodeInput
            length={7}
            value={code}
            onChange={(v) => {
              setCode(v);
              setError("");
            }}
            autoFocus
          />
          <Button
            size="compact"
            className={buttonClass}
            disabled={code.length !== 7 || verifyCode.isPending}
            onClick={() => verifyCode.mutate()}
            data-testid="button-verify-code"
          >
            {verifyCode.isPending ? "Verifying..." : "Verify Code"}
          </Button>
          <div className="flex items-center justify-center gap-3 text-sm">
            <button
              type="button"
              onClick={() => requestCode("resend")}
              disabled={sendReset.isPending}
              className="text-primary underline disabled:text-muted-foreground"
            >
              {sendReset.isPending ? "Sending..." : "Resend code"}
            </button>
            <span className="text-muted-foreground">·</span>
            <button
              type="button"
              onClick={() => {
                setPhase("email");
                setCode("");
                setError("");
                setInfo("");
                writeFlow(null);
              }}
              className="text-primary underline"
            >
              Use a different email
            </button>
          </div>
          {error && <FormErrorMessage>{error}</FormErrorMessage>}
        </div>
      )}

      {phase === "password" && (
        <form
          noValidate
          className="flex w-full flex-col items-center"
          onSubmit={(e) => {
            e.preventDefault();
            submitPassword();
          }}
        >
          <div className="mb-[15px] w-full">
            <PasswordField
              label="New password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setError("");
              }}
              helperText="At least 8 characters."
            />
          </div>
          <div className="mb-[15px] w-full">
            <PasswordField
              label="Confirm password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError("");
              }}
            />
          </div>
          <Button
            type="submit"
            size="compact"
            className={buttonClass}
            disabled={!newPassword || !confirmPassword || resetPassword.isPending}
          >
            {resetPassword.isPending ? "Resetting..." : "Reset Password"}
          </Button>
          {error && <FormErrorMessage>{error}</FormErrorMessage>}
        </form>
      )}

      <button
        type="button"
        onClick={onBack}
        className="mt-4 text-sm text-primary underline hover:text-primary/80"
      >
        Back to sign in
      </button>
    </div>
  );
}
